import { computed } from "@vue/composition-api";
import useMouseSvg from "@/composables/useMouseSvg";
import annotations from "@/store/modules/annotations";
import ui from "@/store/modules/ui";

export default function useEdges(edgeRef, nodeRef) {
  const edges = computed(() => annotations.getEdges);
  const doors = computed(() => annotations.getDoors);

  const remove = (e, { i, j }) => {
    // if not left mouse button, return
    if (e.button !== 0) return;

    annotations.removeDoorArray({ i, j });
    annotations.removeEdge({ i, j });
  };

  const removeDoorTool = (e, { i, j, n }) => {
    if (e.button !== 0) return;

    annotations.removeDoor({ i, j, n });
  };

  const addDoorFromNodeTool = (e, name) => {
    return new Promise(resolve => {
      //
      // Preparation
      // -------------------------------------------------------------
      //
      // if the click wasn't by a left mouse button, return early
      if (e.button !== 0) resolve();
      // Get the index of the clicked node
      const currentTarget = e.currentTarget;
      const nodeIndex = parseInt(currentTarget.dataset.index);
      const { pos: mousePosSvg } = useMouseSvg(e);
      // Get indices of edges adjacent to the selected node
      const edges = annotations.getAdjacentEdges(nodeIndex);
      const edgeIndices = edges.map(edge => edge[1]);
      // Get the refs for the selected edges
      const selectedEdgesRef = [];
      edgeRef.value.forEach(e => {
        const i = parseInt(e.dataset.index);
        if (edgeIndices.findIndex(edgeIndex => edgeIndex === i) >= 0) {
          selectedEdgesRef.push(e);
        }
      });
      // Highlight adjacent edges
      ui.setSelectedEdges(
        edges.map(edge => ({
          i: edge[0].nodes[0],
          j: edge[0].nodes[1]
        }))
      );
      //
      // Create a door on the next click to an adjacent edge
      // -------------------------------------------------------------
      //
      // Get target node's position
      const selectedNode = annotations.getNodes[nodeIndex];
      const x1 = selectedNode.pos.cx;
      const y1 = selectedNode.pos.cy;
      // Define handler
      const edgeClickHandler = e => {
        const targetEdge =
          annotations.getEdges[parseInt(e.target.dataset.index)];
        const mousePos = mousePosSvg(e);
        const [x2, y2] = getNearestPos(targetEdge.pos, mousePos);
        const [r1, r2] = getRelativePos(targetEdge.pos, { x1, y1, x2, y2 });
        annotations.addDoor({
          i: targetEdge.nodes[0],
          j: targetEdge.nodes[1],
          r1,
          r2,
          name
        });
        // Clean up
        cleanup();
        resolve();
      };
      //
      // Create a door on the next click to an adjacent node
      // -------------------------------------------------------------
      //
      // Find neighbor nodes' refs
      const neighborNodeIndices = annotations.neighbors(nodeIndex);
      const neighborNodesRef = [];
      nodeRef.value.forEach(e => {
        const i = parseInt(e.dataset.index);
        if (neighborNodeIndices.findIndex(nodeIndex => nodeIndex === i) >= 0) {
          neighborNodesRef.push(e);
        }
      });
      // Define handler
      const nodeClickHandler = e => {
        const targetNodeIndex = parseInt(e.target.dataset.index);
        const targetEdge = edges.find(
          edge =>
            (edge[0].nodes[0] === nodeIndex &&
              edge[0].nodes[1] === targetNodeIndex) ||
            (edge[0].nodes[1] === nodeIndex &&
              edge[0].nodes[0] === targetNodeIndex)
        );
        annotations.addDoor({
          i: targetEdge[0].nodes[0],
          j: targetEdge[0].nodes[1],
          r1: 0,
          r2: 1,
          name
        });
        // Clean up
        cleanup();
        resolve();
      };
      //
      // Helpers to clean up event handlers and ui
      // -------------------------------------------------------------
      //
      const cleanup = () => {
        selectedEdgesRef.forEach(e => {
          e.removeEventListener("mousedown", edgeClickHandler);
        });
        neighborNodesRef.forEach(e => {
          e.removeEventListener("mousedown", nodeClickHandler);
        });
        ui.setSelectedEdges(null);
      };
      //
      // Attach event handlers
      // -------------------------------------------------------------
      //
      selectedEdgesRef.forEach(e => {
        e.addEventListener("mousedown", edgeClickHandler);
      });
      neighborNodesRef.forEach(e => {
        e.addEventListener("mousedown", nodeClickHandler);
      });
    });
  };

  const addDoorTool = (e, name) => {
    return new Promise(resolve => {
      // if the click wasn't by a left mouse button, return early
      if (e.button !== 0) resolve();
      const currentTarget = e.currentTarget;
      // Edges's index
      const i = parseInt(currentTarget.dataset.index);
      const { pos: mousePosSvg } = useMouseSvg(e);
      const pos = edges.value[i].pos;
      // Select edge
      ui.setSelectedEdges([
        {
          i: edges.value[i].nodes[0],
          j: edges.value[i].nodes[1]
        }
      ]);
      // Find the closest point on edge from mouse
      const mousePos = mousePosSvg(e);
      const [x1, y1] = getNearestPos(pos, mousePos);
      // Create a door on the next click to the same edge
      const clickHandler = e => {
        const mousePos = mousePosSvg(e);
        const [x2, y2] = getNearestPos(pos, mousePos);
        const [r1, r2] = getRelativePos(pos, { x1, y1, x2, y2 });
        annotations.addDoor({
          i: edges.value[i].nodes[0],
          j: edges.value[i].nodes[1],
          r1,
          r2,
          name
        });
        // Clean up event handlers
        cleanEvents();
        // Clean up ui
        ui.setSelectedEdges(null);
        resolve();
      };
      // Function to clean event handler attached to the target edge
      const cleanEvents = () => {
        currentTarget.removeEventListener("mousedown", clickHandler);
      };
      // Add the event listener and wait for next click on the same edge
      currentTarget.addEventListener("mousedown", clickHandler);
    });
  };

  return {
    edgeRef,
    edges,
    doors,
    remove,
    addDoorTool,
    addDoorFromNodeTool,
    removeDoorTool
  };
}

function getNearestPos(edgePos, mousePos) {
  const dx = Math.abs(edgePos.x1 - edgePos.x2);
  const dy = Math.abs(edgePos.y1 - edgePos.y2);
  let x = 0;
  let y = 0;
  // 傾きで条件分岐
  if (dx > dy) {
    x = mousePos.x;
    y =
      edgePos.y1 +
      ((mousePos.x - edgePos.x1) * (edgePos.y2 - edgePos.y1)) /
        (edgePos.x2 - edgePos.x1);
  } else {
    x =
      edgePos.x1 +
      ((mousePos.y - edgePos.y1) * (edgePos.x2 - edgePos.x1)) /
        (edgePos.y2 - edgePos.y1);
    y = mousePos.y;
  }

  return [x, y];
}

function getRelativePos(edgePos, pos) {
  // Edge上でのx1からの距離をedge全体の長さの比率で求める
  const dx = Math.abs(edgePos.x1 - edgePos.x2);
  const dy = Math.abs(edgePos.y1 - edgePos.y2);
  let r1 = 0;
  let r2 = 0;
  if (dx > dy) {
    r1 = (pos.x1 - edgePos.x1) / (edgePos.x2 - edgePos.x1);
    r2 = (pos.x2 - edgePos.x1) / (edgePos.x2 - edgePos.x1);
  } else {
    r1 = (pos.y1 - edgePos.y1) / (edgePos.y2 - edgePos.y1);
    r2 = (pos.y2 - edgePos.y1) / (edgePos.y2 - edgePos.y1);
  }
  return [r1, r2];
}
